import axios, {
  AxiosInstance,
  AxiosPromise,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';

export interface IAxiosAuthRefreshOptions {
  shouldRenew?(error: any): boolean;
  retryInstance?: AxiosInstance;
  onRetry?: (
    requestConfig: InternalAxiosRequestConfig,
  ) => InternalAxiosRequestConfig;
}

export interface IInternalAxiosRequestConfig<TData = any>
  extends InternalAxiosRequestConfig<TData> {
  skipAuthRefresh?: boolean;
}

interface IAxiosError<T = any, TData = any>
  extends AxiosError<T, TData> {
  config: IInternalAxiosRequestConfig<TData>;
}

type RenewalCallbackType = (error?: any) => Promise<any>;

export class RefreshTokenRenewalController {
  private _renewalCall: ReturnType<RenewalCallbackType> | null = null;
  private _instance: AxiosInstance;
  private _renewalCallback: RenewalCallbackType;
  private _options: IAxiosAuthRefreshOptions = {};

  constructor(
    instance: AxiosInstance,
    renewalCallback: RenewalCallbackType,
    options: IAxiosAuthRefreshOptions = {},
  ) {
    this._options = Object.assign(this._options, options);
    this._instance = instance;
    this._renewalCallback = renewalCallback;

    // Create a response interceptor to track certain errors after which we need to renew the token
    this._instance.interceptors.response.use(
      (r) => r,
      async (error: IAxiosError) => {
        if (!this._shouldInterceptError(error)) {
          return Promise.reject(error);
        }

        return this.renewToken(error);
      },
    );

    // Creates request queue interceptor if it does not exist and returns its id.
    this._instance.interceptors.request.use(
      (request: IInternalAxiosRequestConfig) => {
        if (request?.skipAuthRefresh || !this._renewalCall) {
          return request;
        }

        return this._renewalCall
          .then(() => this._options.onRetry?.(request) ?? request)
          .catch(() => {
            throw new axios.Cancel('Request call failed');
          });
      },
    );
  }

  /**
   *  Run refresh token renew logic
   *
   * @param {IAxiosError} [error] - error of failed request to resend after renew
   * @returns {Promise}
   */
  async renewToken(error?: IAxiosError): Promise<any> {
    // If refresh call does not exist, create one
    if (!this._renewalCall) {
      this._renewalCall = this._renewalCallback(error);
    }

    return this._renewalCall
      .then((r) => (error ? this._resendFailedRequest(error) : r))
      .catch((e) => Promise.reject(e))
      .finally(() => this._unsetCache());
  }

  /**
   * Defining whether token should be renewed
   * @param {IAxiosError} error
   *
   * @return {boolean}
   */
  private _shouldInterceptError(error: IAxiosError): boolean {
    if (!error || error.config?.skipAuthRefresh || !error.response) {
      return false;
    }

    return (
      this._options.shouldRenew?.(error) ??
      Number(error.response.status) === 401
    );
  }

  /**
   * Ejects request queue interceptor and unset interceptor cached values.
   *
   * */
  private _unsetCache(): void {
    this._renewalCall = null;
  }

  /**
   * Returns instance that's going to be used when requests are retried
   * @returns {AxiosInstance}
   */
  private _getRetryInstance(): AxiosInstance {
    return this._options.retryInstance || this._instance;
  }

  /**
   * Resend failed axios request.
   *
   * @param {IAxiosError} error
   * @return {AxiosPromise}
   */
  private _resendFailedRequest(error: IAxiosError): AxiosPromise {
    error.config.skipAuthRefresh = true;
    const instance = this._getRetryInstance();
    return instance(error.response!.config);
  }
}
